import React, { useRef, useState } from 'react';
import Pagination from './Pagination';
import TooltipLayer from './Tooltip';
import ListTableHead, { alignClass } from './ListTableHead';
import { useListTableScroll } from './useListTableScroll';
import './ListTable.css';

export const LIST_TABLE_PAGE_SIZE = 25;

// Compares two values of one column, ascending. Empty values go last, and text
// compares as people read it.
function compareValues(column, a, b) {
  const aEmpty = a === null || a === undefined || a === '';
  const bEmpty = b === null || b === undefined || b === '';
  if (aEmpty || bEmpty) return aEmpty === bEmpty ? 0 : aEmpty ? 1 : -1;
  if (column.sort === 'number' || column.sort === 'date') return a - b;
  return String(a).localeCompare(String(b), 'en', { numeric: true, sensitivity: 'base' });
}

/**
 * Sorts a copy. Empty values go last either way. Rows that tie fall back to
 * `tieBreak`, the table's first column, A to Z, so a column where many rows
 * share a value (Status, Usage) still reads in a known order.
 */
export function sortRows(rows, sort, column, tieBreak) {
  if (!sort || !column?.value) return rows.slice();
  const dir = sort.dir === 'asc' ? 1 : -1;
  return rows.slice().sort((x, y) => {
    const a = column.value(x);
    const b = column.value(y);
    const aEmpty = a === null || a === undefined || a === '';
    const bEmpty = b === null || b === undefined || b === '';
    const order = aEmpty || bEmpty ? compareValues(column, a, b) : compareValues(column, a, b) * dir;
    if (order || !tieBreak?.value || tieBreak === column) return order;
    return compareValues(tieBreak, tieBreak.value(x), tieBreak.value(y));
  });
}

/** A new column sorts numbers high to low and text and dates A to Z first; the same column flips. */
export function nextSort(sort, column) {
  if (sort?.key === column.key) return { key: column.key, dir: sort.dir === 'asc' ? 'desc' : 'asc' };
  return { key: column.key, dir: column.sort === 'number' ? 'desc' : 'asc' };
}

// The narrowest a track can go: '200px' is 200, 'minmax(160px, 1fr)' is 160.
const minOf = (width) => {
  const m = /minmax\(\s*(\d+)px/.exec(width) || /^(\d+)px$/.exec(String(width).trim());
  return m ? Number(m[1]) : 0;
};

/**
 * PRD-1700: the ResUI List table, the one table for every list in the Console
 * (Navy Phase 0 handoff, "Tables", ported from prototype/list-table.js).
 *
 * - The first column is pinned, so its width must be px. Once the table has
 *   moved under it, one fade runs down its edge; while there's more to the
 *   right, another shows there. Both are 20 wide on the same ramp.
 * - The head sticks under the app header and follows the columns sideways off
 *   a scroll timeline, so it moves with the scroll rather than a frame behind.
 *   Where there's no scroll timeline it falls back to a mirrored scroller.
 * - Every column with a `sort` type sorts from its header. Focus stays on the
 *   header and the new order is announced.
 * - Over `pageSize` rows it pages; a new sort or `resetKey` goes back to page 1,
 *   and `focusRowKey` (a row an action is about to focus) brings its page up.
 * - No rows renders nothing, head and all: the caller's count line says so.
 * - A row or cell with `data-tip` gets the ResUI Tooltip, which sits outside the
 *   scroller so it is never clipped.
 * - With `onRowClick`, each row opens its record: from a click, or Enter or
 *   Space on the focused row, which takes the focus ring 2 outside, above its
 *   neighbours. `rowLabel` reads the row's values for screen readers. A row
 *   that is a button can't sit in a table, so then, as in the prototype, the
 *   table drops its table roles. Clicks and keys from a control inside the row
 *   (its menu) stay with that control.
 * - `detail(row)` returns `{ id, open, content }` for a record that opens
 *   under its row (the Activity log's). It is laid out as its row: a pinned
 *   cell under the first column, and the content from the second column on,
 *   scrolling sideways with the row's cells (ResUI Design Decisions, Tables).
 *   It stays in the page while closed, so the control that opens it can name
 *   it. The row takes `.is-open` from `rowProps` while it shows.
 *
 * columns: [{ key, title, label, width, sort: 'text' | 'number' | 'date', align, value(row), render(row), cellClassName }]
 * `label` is the column's name as plain text, for a `title` that is a node.
 */
function ListTable({
  label,
  columns,
  rows,
  rowKey,
  rowProps,
  onRowClick,
  rowLabel,
  sort,
  onSortChange,
  pageSize = LIST_TABLE_PAGE_SIZE,
  resetKey,
  focusRowKey,
  detail,
}) {
  const rootRef = useRef(null);
  const headRef = useRef(null);
  const scrollRef = useRef(null);
  const [announcement, setAnnouncement] = useState('');
  const hasRows = rows.length > 0;
  const tableRoles = !onRowClick;
  const tableRole = (role) => (tableRoles ? role : undefined);

  // A new sort, search or filter starts again at the top, in the same render:
  // the page belongs to the sort and filters it was chosen under.
  const signature = `${sort?.key}|${sort?.dir}|${resetKey}`;
  // Cleared as soon as the signature moves, so returning to an earlier one
  // doesn't bring its old page back.
  const [paging, setPaging] = useState({ page: 1, signature });
  if (paging.signature !== signature) setPaging({ page: 1, signature });
  const page = paging.signature === signature ? paging.page : 1;
  const setPage = (next) => setPaging({ page: next, signature });

  const sortColumn = columns.find((c) => c.key === sort?.key);
  const sorted = sortRows(rows, sort, sortColumn, columns[0]);
  const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
  // A row an action is about to focus (Retry) can move pages under the sort, as
  // its status changes: the table turns to its page, so focus finds it.
  const focusIndex = focusRowKey == null ? -1 : sorted.findIndex((row) => rowKey(row) === focusRowKey);
  const focusPage = focusIndex < 0 ? null : Math.floor(focusIndex / pageSize) + 1;
  if (focusPage && focusPage !== Math.min(page, pages)) setPaging({ page: focusPage, signature });
  const current = focusPage || Math.min(page, pages);
  const visible = sorted.slice((current - 1) * pageSize, current * pageSize);

  useListTableScroll(rootRef, headRef, scrollRef, hasRows);

  if (!hasRows) return null;

  const handleSort = (column) => {
    const next = nextSort(sort, column);
    onSortChange(next);
    setAnnouncement(`Sorted by ${column.label || column.title}, ${next.dir === 'asc' ? 'ascending' : 'descending'}`);
  };

  // Only the row's own click or key: not one from its menu, which may be
  // portalled elsewhere in the page but still bubbles here through React.
  const fromRow = (e) => e.currentTarget.contains(e.target)
    && (e.target === e.currentTarget || !e.target.closest('button, a, input, select, textarea, [role="menuitem"]'));
  // A key from the row, or from a part of it focus was sent to (out of the tab
  // order, like a name an action focuses): not from a control inside it.
  const keyFromRow = (e) => e.target === e.currentTarget
    || (e.currentTarget.contains(e.target) && e.target.tabIndex < 0);
  const rowHandlers = (row) => (onRowClick ? {
    role: 'button',
    tabIndex: 0,
    'aria-label': rowLabel?.(row),
    onClick: (e) => { if (fromRow(e)) onRowClick(row); },
    onKeyDown: (e) => {
      if (!keyFromRow(e) || (e.key !== 'Enter' && e.key !== ' ')) return;
      e.preventDefault();
      onRowClick(row);
    },
  } : { role: 'row' });

  const pin = /^(\d+)px$/.exec(String(columns[0].width).trim());
  const style = {
    '--list-table-cols': columns.map((c) => c.width).join(' '),
    '--list-table-min': `${columns.reduce((n, c) => n + minOf(c.width), 0)}px`,
    '--list-table-pin': `${pin ? pin[1] : 0}px`,
  };

  return (
    <>
      <div ref={rootRef} className={`list-table${onRowClick ? ' list-table--interactive' : ''}`} role={tableRole('table')} aria-label={tableRoles ? label : undefined} data-fade="none" data-scrolled="false" style={style}>
        <ListTableHead headRef={headRef} columns={columns} sort={sort} onSort={onSortChange ? handleSort : null} tableRoles={tableRoles} />
        {/* Focusable so the table can be scrolled sideways from the keyboard. */}
        <div ref={scrollRef} className="list-table-scroll" role={tableRoles ? 'rowgroup' : 'region'} tabIndex={0} aria-label={tableRoles ? `${label} rows` : label}>
          <div className="list-table-body">
            {visible.map((row) => {
              const { className, ...extra } = rowProps?.(row) || {};
              const record = detail?.(row);
              return (
                <React.Fragment key={rowKey(row)}>
                  <div className={`list-table-row${className ? ` ${className}` : ''}`} {...rowHandlers(row)} {...extra}>
                    {columns.map((column) => (
                      <div key={column.key} role={tableRole('cell')} className={`${column.cellClassName || ''}${alignClass(column.align)}`.trim() || undefined}>
                        {column.render ? column.render(row) : column.value?.(row)}
                      </div>
                    ))}
                  </div>
                  {record && (
                    <div id={record.id} className="list-table-detail" role={tableRole('row')} hidden={!record.open}>
                      <div role={tableRole('cell')} />
                      <div role={tableRole('cell')} className="list-table-detail-body">{record.content}</div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
        <div className="list-table-pinfade" aria-hidden="true" />
        <div className="list-table-gutter-left" aria-hidden="true" />
        <TooltipLayer scopeRef={rootRef} />
      </div>
      {/* Outside the table, which may own only rows and rowgroups. */}
      <span className="list-table-sr" aria-live="polite">{announcement}</span>
      <Pagination currentPage={current} totalPages={pages} onPageChange={setPage} />
    </>
  );
}

export default ListTable;
